<template>
  <div class="search-panel">
    <div class="search-panel__header">
      <div class="search-panel__input-wrap">
        <CIcon
          name="cil-search"
          size="custom-size"
          class="search-panel__search-icon"
        />
        <input
          ref="searchInput"
          v-model="searchTerm"
          type="text"
          class="search-panel__input"
          placeholder="Search by name, email, phone, or company..."
          @keydown.enter="performSearch"
        >
        <transition name="fade">
          <button
            v-if="searchTerm"
            class="search-panel__clear-btn"
            title="Clear search"
            @click="clearSearch"
          >
            <CIcon
              name="cil-x"
              size="custom-size"
            />
          </button>
        </transition>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="isLoading"
        class="search-panel__loading"
      >
        <CSpinner
          color="info"
          grow
        />
        <span class="search-panel__loading-text">Searching...</span>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="hasSearched && !isLoading && noResultsFound"
        class="search-panel__empty"
      >
        <CIcon
          name="cil-find-in-page"
          size="custom-size"
          class="search-panel__empty-icon"
        />
        <p class="search-panel__empty-text">
          No results found for "{{ lastSearchedTerm }}"
        </p>
      </div>
    </transition>

    <template v-if="!isLoading && hasResults">
      <div class="sp-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="sp-tabs__btn"
          :class="{ 'sp-tabs__btn--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
          <span
            v-if="tab.count > 0"
            class="sp-tabs__count"
          >{{ tab.count }}</span>
        </button>
      </div>

      <div class="sp-list">
        <template v-if="activeTab === 'buyers'">
          <div
            v-for="(result, index) in allBuyerResults"
            :key="'b-' + index"
            class="sp-list__item"
          >
            <div class="sp-list__info">
              <div class="sp-list__title-row">
                <CIcon
                  name="cil-building"
                  size="custom-size"
                  class="sp-list__type-icon"
                />
                <router-link
                  :to="result._route"
                  target="_blank"
                  class="sp-list__link"
                >
                  <span v-html="highlight(result.name)" />
                </router-link>
                <span
                  v-if="result._status"
                  class="sp-badge"
                  :class="'sp-badge--' + result._statusClass"
                >{{ result._status }}</span>
              </div>
              <div
                v-if="getRepSummary(result)"
                class="sp-list__rep"
              >
                <CIcon
                  name="cil-user"
                  size="custom-size"
                  class="sp-list__rep-icon"
                />
                <span v-html="highlight(getRepSummary(result))" />
              </div>
            </div>
            <div class="sp-list__actions">
              <button
                v-if="result.ringbaAccountId"
                class="sp-list__action-btn"
                title="Filter Charts"
                @click="$emit('account-selected', result.ringbaAccountId)"
              >
                <CIcon
                  name="cil-filter"
                  size="custom-size"
                />
              </button>
              <button
                v-if="result.ringbaAccountId"
                class="sp-list__copy"
                title="Copy Ringba Account ID"
                @click="copyAccountIdToClipboard(result.ringbaAccountId)"
              >
                <CIcon
                  name="cil-copy"
                  size="custom-size"
                />
              </button>
            </div>
          </div>
          <div
            v-if="!allBuyerResults.length"
            class="sp-list__empty"
          >
            No buyers found
          </div>
        </template>

        <template v-if="activeTab === 'sellers'">
          <div
            v-for="(result, index) in allSellerResults"
            :key="'s-' + index"
            class="sp-list__item"
          >
            <div class="sp-list__info">
              <div class="sp-list__title-row">
                <CIcon
                  name="cil-building"
                  size="custom-size"
                  class="sp-list__type-icon"
                />
                <router-link
                  :to="result._route"
                  target="_blank"
                  class="sp-list__link"
                >
                  <span v-html="highlight(result.name)" />
                </router-link>
                <span
                  v-if="result._status"
                  class="sp-badge"
                  :class="'sp-badge--' + result._statusClass"
                >{{ result._status }}</span>
              </div>
              <div
                v-if="getRepSummary(result)"
                class="sp-list__rep"
              >
                <CIcon
                  name="cil-user"
                  size="custom-size"
                  class="sp-list__rep-icon"
                />
                <span v-html="highlight(getRepSummary(result))" />
              </div>
            </div>
            <button
              v-if="result.ringbaAccountId"
              class="sp-list__copy"
              title="Copy Ringba Account ID"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              <CIcon
                name="cil-copy"
                size="custom-size"
              />
            </button>
          </div>
          <div
            v-if="!allSellerResults.length"
            class="sp-list__empty"
          >
            No sellers found
          </div>
        </template>

        <template v-if="activeTab === 'users'">
          <div
            v-for="(user, index) in filteredUsers"
            :key="'u-' + index"
            class="sp-list__item"
          >
            <div class="sp-list__info">
              <div class="sp-list__title-row">
                <CIcon
                  name="cil-user"
                  size="custom-size"
                  class="sp-list__type-icon"
                />
                <span
                  class="sp-list__link sp-list__link--plain"
                  v-html="highlight(user.name || '')"
                />
                <span
                  v-if="user.status"
                  class="sp-badge"
                  :class="'sp-badge--' + user.status.toLowerCase()"
                >{{ user.status }}</span>
              </div>
              <div class="sp-list__rep">
                <span v-html="highlight(user.email || '')" />
              </div>
            </div>
          </div>
          <div
            v-if="!filteredUsers.length"
            class="sp-list__empty"
          >
            No users found
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { showSuccessMessage } from '@/notification-utils'
import AccountsAPI from '@/api/accounts.api'
import UserAPI from '@/api/users.api'

export default {
  name: 'SearchAccountOrUser',

  mixins: [copyToClipboardMixin],

  data() {
    return {
      isLoading: false,
      searchTerm: '',
      hasSearched: false,
      lastSearchedTerm: '',
      activeTab: 'buyers',
      registrationRequestsLoaded: false,
      pendingBuyerResults: [],
      pendingSellerResults: [],
      accountNameSearchBuyers: [],
      accountNameSearchSellers: [],
      allSystemUsers: [],
      usersLoaded: false,
      debounceTimer: null
    }
  },

  computed: {
    ...mapState('accounts', [
      'companyNameSearchResults',
      'accountsSearchByUserResults',
      'buyerAccounts',
      'sellerAccounts'
    ]),

    ...mapState('registrationRequests', [
      'buyerRegistrationRequests',
      'sellerRegistrationRequests'
    ]),

    allBuyerAccounts() {
      if (!this.lastSearchedTerm) {
        return []
      }

      const term = this.lastSearchedTerm.toLowerCase()

      return this.dedupeAndNormalize([
        ...this.accountsSearchByUserResults.buyers
          .filter(a => a && this.matchesVisibleFields(a, term))
          .map(a => this.normalizeAccount(a, 'search', 'buyer')),
        ...this.accountNameSearchBuyers
          .filter(a => a && this.matchesVisibleFields(a, term))
          .map(a => this.normalizeAccount(a, 'search', 'buyer')),
        ...this.companyNameSearchResults
          .filter(r => r.buyerAccountId)
          .map(r => this.normalizeAccount({
            id: r.buyerAccountId, name: r.companyName,
            ringbaAccountId: r.ringbaAccountId, representative: r.representative
          }, 'search', 'buyer')),
        ...this.pendingBuyerResults
          .filter(a => a && (
            a.name?.toLowerCase().includes(term)
            || this.matchesRepresentative(a.representative, term)
          ))
          .map(a => this.normalizeAccount(a, 'pending', 'buyer')),
        ...(Array.isArray(this.buyerAccounts) ? this.buyerAccounts : [])
          .filter(a => a && (
            a.name?.toLowerCase().includes(term)
            || this.matchesRepresentative(a.representative, term)
          ))
          .map(a => this.normalizeAccount(a, 'local', 'buyer'))
      ])
    },

    allSellerAccounts() {
      if (!this.lastSearchedTerm) {
        return []
      }

      const term = this.lastSearchedTerm.toLowerCase()

      return this.dedupeAndNormalize([
        ...this.accountsSearchByUserResults.sellers
          .filter(a => a && this.matchesVisibleFields(a, term))
          .map(a => this.normalizeAccount(a, 'search', 'seller')),
        ...this.accountNameSearchSellers
          .filter(a => a && this.matchesVisibleFields(a, term))
          .map(a => this.normalizeAccount(a, 'search', 'seller')),
        ...this.companyNameSearchResults
          .filter(r => r.sellerAccountId)
          .map(r => this.normalizeAccount({
            id: r.sellerAccountId, name: r.companyName,
            ringbaAccountId: r.ringbaAccountId, representative: r.representative
          }, 'search', 'seller')),
        ...this.pendingSellerResults
          .filter(a => a && (
            a.name?.toLowerCase().includes(term)
            || this.matchesRepresentative(a.representative, term)
          ))
          .map(a => this.normalizeAccount(a, 'pending', 'seller')),
        ...(Array.isArray(this.sellerAccounts) ? this.sellerAccounts : [])
          .filter(a => a && (
            a.name?.toLowerCase().includes(term)
            || this.matchesRepresentative(a.representative, term)
          ))
          .map(a => this.normalizeAccount(a, 'local', 'seller'))
      ])
    },

    buyerAppResults() {
      if (!this.lastSearchedTerm) {
        return []
      }

      const term = this.lastSearchedTerm.toLowerCase()

      const apiResults = this.companyNameSearchResults
        .filter(r => r.buyerRegistrationId)
        .map(r => this.normalizeApp(r.buyerRegistrationId, r.companyName, r.representative, r.status, 'buyer'))

      const usedIds = new Set(apiResults.map(r => r.id))
      const clientResults = this.buyerRegistrationRequests
        .filter(req => {
          if (!req || usedIds.has(req.id)) {
            return false
          }

          return (req.representative && this.matchesRepresentative(req.representative, term))
            || req.companyName?.toLowerCase().includes(term)
        })
        .map(req => this.normalizeApp(req.id, req.companyName, req.representative, req.status, 'buyer'))

      return [...apiResults, ...clientResults]
    },

    sellerAppResults() {
      if (!this.lastSearchedTerm) {
        return []
      }

      const term = this.lastSearchedTerm.toLowerCase()

      const apiResults = this.companyNameSearchResults
        .filter(r => r.sellerRegistrationId)
        .map(r => this.normalizeApp(r.sellerRegistrationId, r.companyName, r.representative, r.status, 'seller'))

      const usedIds = new Set(apiResults.map(r => r.id))
      const clientResults = this.sellerRegistrationRequests
        .filter(req => {
          if (!req || usedIds.has(req.id)) {
            return false
          }

          return (req.representative && this.matchesRepresentative(req.representative, term))
            || req.companyName?.toLowerCase().includes(term)
        })
        .map(req => this.normalizeApp(req.id, req.companyName, req.representative, req.status, 'seller'))

      return [...apiResults, ...clientResults]
    },

    allBuyerResults() {
      return [...this.allBuyerAccounts, ...this.buyerAppResults]
    },

    allSellerResults() {
      return [...this.allSellerAccounts, ...this.sellerAppResults]
    },

    filteredUsers() {
      if (!this.lastSearchedTerm) {
        return []
      }

      const term = this.lastSearchedTerm.toLowerCase()
      return this.allSystemUsers.filter(u => {
        if (!u) {
          return false
        }

        return (u.name || '').toLowerCase().includes(term)
          || (u.email || '').toLowerCase().includes(term)
      })
    },

    tabs() {
      const t = [
        { key: 'buyers', label: 'Buyers', count: this.allBuyerResults.length },
        { key: 'sellers', label: 'Sellers', count: this.allSellerResults.length }
      ]
      if (this.filteredUsers.length) {
        t.push({ key: 'users', label: 'Users', count: this.filteredUsers.length })
      }

      return t
    },

    noResultsFound() {
      return this.allBuyerResults.length === 0
        && this.allSellerResults.length === 0
        && this.filteredUsers.length === 0
    },

    hasResults() {
      if (!this.hasSearched) {
        return false
      }

      return !this.noResultsFound
    }
  },

  watch: {
    searchTerm(val) {
      clearTimeout(this.debounceTimer)
      if (!val || !val.trim()) {
        this.hasSearched = false
        return
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch()
      }, 600)
    }
  },

  beforeDestroy() {
    clearTimeout(this.debounceTimer)
  },

  methods: {
    ...mapActions('accounts', [
      'searchByCompanyName',
      'searchAccountsByUser'
    ]),

    ...mapActions('registrationRequests', [
      'getRegistrationRequests',
    ]),

    normalizeAccount(account, source, type) {
      if (!account) {
        return null
      }

      let status = ''
      let statusClass = ''

      if (source === 'pending') {
        status = 'Pending'
        statusClass = 'pending'
      } else if (account.enabled === false) {
        status = 'Disabled'
        statusClass = 'disabled'
      }

      const routeName = type === 'seller' ? 'SellerAccountDetails' : 'BuyerAccountDetails'

      return {
        id: account.id,
        name: account.name || account.companyName,
        ringbaAccountId: account.ringbaAccountId,
        representative: account.representative,
        _status: status,
        _statusClass: statusClass,
        _route: { name: routeName, params: { id: account.id } }
      }
    },

    normalizeApp(id, name, representative, status, type) {
      const s = (status || '').toLowerCase()
      let statusClass = 'pending'
      if (s === 'approved') {
        statusClass = 'approved'
      } else if (s === 'rejected') {
        statusClass = 'rejected'
      } else if (s === 'waitlisted') {
        statusClass = 'waitlisted'
      }

      return {
        id,
        name: name || '',
        representative,
        _status: status || 'Application',
        _statusClass: statusClass,
        _route: { name: 'RegistrationRequestDetails', params: { id, type } }
      }
    },

    dedupeAndNormalize(items) {
      const seen = new Set()
      return items.filter(a => {
        if (!a || !a.id || seen.has(a.id)) {
          return false
        }

        seen.add(a.id)
        return true
      })
    },

    getRepSummary(result) {
      const rep = result.representative
      if (!rep) {
        return ''
      }

      const name = `${rep.firstName || ''} ${rep.lastName || ''}`.trim()
      const email = rep.email || ''
      const parts = []
      if (name) {
        parts.push(name)
      }

      if (email) {
        parts.push(email)
      }

      return parts.join(' | ')
    },

    matchesRepresentative(rep, term) {
      if (!rep) {
        return false
      }

      const name = `${rep.firstName || ''} ${rep.lastName || ''}`.toLowerCase()
      const email = (rep.email || '').toLowerCase()
      const phone = (rep.cellPhone || '').replace(/\D/g, '')
      const termDigits = term.replace(/\D/g, '')

      return name.includes(term)
        || email.includes(term)
        || (termDigits.length >= 4 && phone.includes(termDigits))
    },

    matchesVisibleFields(result, term) {
      if ((result.name || '').toLowerCase().includes(term)) {
        return true
      }

      return this.matchesRepresentative(result.representative, term)
    },

    highlight(text) {
      if (!text || !this.lastSearchedTerm) {
        return text || ''
      }

      const escaped = this.lastSearchedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text.replace(new RegExp('(' + escaped + ')', 'gi'), '<mark>$1</mark>')
    },

    copyAccountIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Ringba Account ID copied to clipboard')
    },

    clearSearch() {
      this.searchTerm = ''
      this.hasSearched = false
      this.lastSearchedTerm = ''
      this.$refs.searchInput?.focus()
    },

    async performSearch() {
      const term = this.searchTerm.trim()
      if (!term || term.length < 2) {
        return
      }

      try {
        this.isLoading = true
        this.lastSearchedTerm = term

        const loads = [
          this.searchAccountsByUser(term),
          this.searchByCompanyName(term),
          AccountsAPI.searchBuyerAccounts(term).then(r => {
            this.accountNameSearchBuyers = (r.data || []).filter(Boolean)
          }).catch(() => {
            this.accountNameSearchBuyers = [] 
          }),
          AccountsAPI.searchSellerAccounts(term).then(r => {
            this.accountNameSearchSellers = (r.data || []).filter(Boolean)
          }).catch(() => {
            this.accountNameSearchSellers = [] 
          }),
          AccountsAPI.getPendingBuyerAccounts().then(r => {
            this.pendingBuyerResults = (r.data || []).filter(Boolean)
          }).catch(() => {
            this.pendingBuyerResults = [] 
          }),
          AccountsAPI.getPendingSellerAccounts().then(r => {
            this.pendingSellerResults = (r.data || []).filter(Boolean)
          }).catch(() => {
            this.pendingSellerResults = [] 
          })
        ]

        if (!this.registrationRequestsLoaded) {
          loads.push(this.getRegistrationRequests({ registrationType: 'buyer' }))
          loads.push(this.getRegistrationRequests({ registrationType: 'seller' }))
        }

        if (!this.usersLoaded) {
          loads.push(
            UserAPI.getUsers('Admin').then(r => {
              this.allSystemUsers = (r.data || []).filter(Boolean)
            }).catch(() => {
              this.allSystemUsers = [] 
            })
          )
        }

        await Promise.all(loads)
        this.registrationRequestsLoaded = true
        this.usersLoaded = true
        this.hasSearched = true
      } catch (err) {
        // API error handled by http client
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.search-panel {
  &__header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8f9fa;
    padding-bottom: 8px;
  }

  &__input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__search-icon {
    position: absolute;
    left: 12px;
    width: 16px;
    height: 16px;
    color: #9da5b1;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    padding: 9px 36px 9px 36px;
    border: 2px solid #d8dbe0;
    border-radius: 8px;
    font-size: 14px;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    outline: none;

    &:focus {
      border-color: #321fdb;
      box-shadow: 0 0 0 3px rgba(50, 31, 219, 0.12);
    }

    &::placeholder {
      color: #9da5b1;
    }
  }

  &__clear-btn {
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: #e9ecef;
    color: #6c757d;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #dee2e6;
    }

    .c-icon {
      width: 12px;
      height: 12px;
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    gap: 12px;
  }

  &__loading-text {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 16px;
    color: #9da5b1;
  }

  &__empty-icon {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  &__empty-text {
    font-size: 14px;
    margin: 0;
  }
}

/* Result tabs */
.sp-tabs {
  display: flex;
  gap: 2px;
  margin: 10px 0 0;
  border-bottom: 2px solid #e9ecef;

  &__btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 7px 4px;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    background: none;
    color: #8a93a2;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
      color: #495057;
    }

    &--active {
      color: #321fdb;
      border-bottom-color: #321fdb;
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #e9ecef;
    font-size: 10px;
    font-weight: 700;
    color: #495057;

    .sp-tabs__btn--active & {
      background: #eef0ff;
      color: #321fdb;
    }
  }
}

/* Result list */
.sp-list {
  background: #fff;
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 6px 6px;

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 12px;
    gap: 8px;
    transition: background 0.12s ease;

    & + & {
      border-top: 1px solid #f0f2f5;
    }

    &:hover {
      background: #f8f9fb;
    }
  }

  &__info {
    min-width: 0;
    overflow: hidden;
    flex: 1;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;

    @media (max-width: 575.98px) {
      flex-wrap: wrap;
      row-gap: 4px;
    }
  }

  &__link {
    font-size: 13px;
    font-weight: 500;
    color: #321fdb;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      color: #1f13a0;
      text-decoration: underline;
    }

    &--plain {
      color: #2c384a;
      cursor: default;

      &:hover {
        color: #2c384a;
        text-decoration: none;
      }
    }
  }

  &__type-icon {
    width: 13px;
    height: 13px;
    color: #8a93a2;
    flex-shrink: 0;
  }

  &__rep {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #8a93a2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 2px;
    padding-left: 1px;
  }

  &__rep-icon {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    color: #adb5bd;
  }

  mark {
    background: #fff3cd;
    color: inherit;
    padding: 0 1px;
    border-radius: 2px;
  }

  &__copy {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #9da5b1;
    cursor: pointer;
    transition: all 0.12s ease;

    &:hover {
      background: #e9ecef;
      color: #321fdb;
    }

    .c-icon {
      width: 12px;
      height: 12px;
    }

    @media (max-width: 575.98px) {
      width: 32px;
      height: 32px;

      .c-icon {
        width: 14px;
        height: 14px;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #321fdb;
    cursor: pointer;
    transition: all 0.12s ease;

    &:hover {
      background: #eef0ff;
    }

    .c-icon {
      width: 12px;
      height: 12px;
    }

    @media (max-width: 575.98px) {
      width: 32px;
      height: 32px;

      .c-icon {
        width: 14px;
        height: 14px;
      }
    }
  }

  &__empty {
    padding: 20px 12px;
    text-align: center;
    font-size: 13px;
    color: #9da5b1;
  }
}

/* Status badges */
.sp-badge {
  flex-shrink: 0;
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 16px;

  &--active {
    background: #d4edda;
    color: #155724;
  }

  &--disabled {
    background: #f8d7da;
    color: #721c24;
  }

  &--pending {
    background: #fff3cd;
    color: #856404;
  }

  &--approved {
    background: #d4edda;
    color: #155724;
  }

  &--rejected {
    background: #f8d7da;
    color: #721c24;
  }

  &--waitlisted {
    background: #d1ecf1;
    color: #0c5460;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
